import { monthKeyFromDate, normalizeText, safeParseDate } from "../utils/historyUtils";

import { formatDate } from "../../../../../commons/util/HallAppUtil";
// hooks/useCustomerHistoryView.js
import { useMemo } from "react";

export default function useCustomerHistoryView({ history, q, period, groupMode, sortMode }) {
  const filteredHistory = useMemo(() => {
    const now = new Date();
    const cutoff = (() => {
      if (period === "30D") return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      if (period === "90D") return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 90);
      if (period === "12M") return new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
      return null;
    })();

    const query = normalizeText(q);

    const base = (history || [])
      .map((it) => ({ ...it, _dateObj: safeParseDate(it.serviceDate) }))
      .filter((it) => {
        if (cutoff && it._dateObj) {
          if (it._dateObj.getTime() < cutoff.getTime()) return false;
        }

        if (!query) return true;

        const hay = normalizeText(`${it.serviceName || ""} ${it.employeeName || ""} ${it.notes || ""}`);
        return hay.includes(query);
      });

    return base.sort((a, b) => {
      const av = Number(a.value) || 0;
      const bv = Number(b.value) || 0;
      const ad = a._dateObj?.getTime() || 0;
      const bd = b._dateObj?.getTime() || 0;

      switch (sortMode) {
        case "DATE_ASC":
          return ad - bd;
        case "VALUE_DESC":
          return bv - av;
        case "VALUE_ASC":
          return av - bv;
        case "DATE_DESC":
        default:
          return bd - ad;
      }
    });
  }, [history, q, period, sortMode]);

  const groupedHistory = useMemo(() => {
    if (groupMode === "NONE") return [];

    const map = new Map();
    for (const it of filteredHistory) {
      const key = monthKeyFromDate(it._dateObj);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }

    const entries = Array.from(map.entries()).map(([key, items]) => {
      items.sort((a, b) => (b._dateObj?.getTime() || 0) - (a._dateObj?.getTime() || 0));
      return [key, items];
    });

    entries.sort((a, b) => {
      const ad = a[1]?.[0]?._dateObj?.getTime() || 0;
      const bd = b[1]?.[0]?._dateObj?.getTime() || 0;
      return bd - ad;
    });

    return entries;
  }, [filteredHistory, groupMode]);

  const stats = useMemo(() => {
    const totalSpent = filteredHistory.reduce((acc, it) => acc + (Number(it.value) || 0), 0);
    const totalVisits = filteredHistory.length;

    const visitsByMonth = new Map();
    const spentByMonth = new Map();

    for (const it of filteredHistory) {
      const key = monthKeyFromDate(it._dateObj);
      visitsByMonth.set(key, (visitsByMonth.get(key) || 0) + 1);
      spentByMonth.set(key, (spentByMonth.get(key) || 0) + (Number(it.value) || 0));
    }

    const months = visitsByMonth.size || 0;
    const avgPerMonth = months === 0 ? 0 : Math.round(totalVisits / months);
    const avgTicket = totalVisits === 0 ? 0 : Math.round((totalSpent / totalVisits) * 100) / 100;

    let bestMonth = null;
    let bestMonthValue = -1;
    for (const [k, v] of spentByMonth.entries()) {
      if (v > bestMonthValue) {
        bestMonthValue = v;
        bestMonth = k;
      }
    }

    const last = filteredHistory
      .filter((x) => x._dateObj)
      .sort((a, b) => (b._dateObj?.getTime() || 0) - (a._dateObj?.getTime() || 0))[0];

    return {
      totalSpent,
      totalVisits,
      avgPerMonth,
      avgTicket,
      bestMonth,
      bestMonthValue: bestMonthValue > 0 ? bestMonthValue : 0,
      lastVisitDate: last?._dateObj ? formatDate(last.serviceDate) : null,
    };
  }, [filteredHistory]);

  return { filteredHistory, groupedHistory, stats };
}
